import type { ReactNode } from 'react';
import { forwardRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  type SxProps,
  type Theme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, BoxProps } from '../../Layout/Box';
import { Typography } from '../../Typography';
import { theme } from '../../Theme/theme';

export interface ExpandableSectionProps
  extends Omit<BoxProps, 'children' | 'onChange' | 'color' | 'component' | 'ref'> {
  /**
   * The title of the container displayed in the header.
   */
  title?: string;
  /**
   * Optional subtitle displayed below the title.
   */
  subtitle?: string;
  /**
   * Action element displayed in the header (e.g., close icon, menu button).
   */
  action?: ReactNode;
  /**
   * If `true`, displays the container as a collapsible accordion.
   * @default false
   */
  collapsible?: boolean;
  /**
   * If `true`, the accordion is expanded by default (only applies when collapsible is true).
   * @default true
   */
  defaultExpanded?: boolean;
  /**
   * Controlled expanded state (only applies when collapsible is true).
   * When provided, the component becomes controlled.
   */
  expanded?: boolean;
  /**
   * Callback fired when the accordion is toggled (only applies when collapsible is true).
   */
  onToggle?: () => void;
  /**
   * The content of the container.
   */
  children?: ReactNode;
  /**
   * Custom accordion summary content (only applies when collapsible is true).
   */
  summaryContent?: ReactNode;
  /**
   * If `true`, shows expand/collapse icon in the header.
   * @default true
   */
  showExpandIcon?: boolean;
}

const getHeaderStyles = (isExpanded: boolean): SxProps<Theme> => ({
  padding: isExpanded ? '0 16px' : 2,
  transition: 'padding 0.3s ease-in-out',
});

/**
 * ExpandableSection component.
 *
 * Renders either a static section with optional header (title, subtitle, action) or a collapsible
 * accordion. Supports controlled (expanded + onToggle) and uncontrolled (defaultExpanded) modes.
 *
 * @example
 * ```tsx
 * // Static section
 * <ExpandableSection title="Details" subtitle="Optional subtitle">
 *   <Typography variant="body2">Content here</Typography>
 * </ExpandableSection>
 *
 * // Collapsible (uncontrolled)
 * <ExpandableSection collapsible title="Work" defaultExpanded={false}>
 *   <WorkList />
 * </ExpandableSection>
 *
 * // Collapsible (controlled)
 * <ExpandableSection collapsible expanded={open} onToggle={() => setOpen(!open)} title="Filters">
 *   <FilterForm />
 * </ExpandableSection>
 * ```
 */
export const ExpandableSection = forwardRef<HTMLDivElement, ExpandableSectionProps>(
  function ExpandableSection(
    {
      title,
      subtitle,
      action,
      collapsible = false,
      defaultExpanded = true,
      expanded,
      onToggle,
      children,
      summaryContent,
      showExpandIcon = true,
      sx,
      ...other
    },
    ref
  ) {
    const hasHeader = Boolean(title || subtitle || action);
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

    const isExpanded = expanded !== undefined ? expanded : internalExpanded;

    const handleChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
      if (expanded === undefined) {
        setInternalExpanded(isExpanded);
      }
      onToggle?.();
    };

    const containerStyles: SxProps<Theme> = {
      background: theme.palette.common.gradientLightGray,
    };

    const accordionDetailsStyles: SxProps<Theme> = {
      background: theme.palette.common.gradientLightGray,
      padding: 0,
      margin: 0,
    };

    const accordionHeaderStyles = (expandedState: boolean): SxProps<Theme> => ({
      background: expandedState ? 'transparent' : theme.palette.common.gradientLightGray,
      margin: 0,
      '&:before': {
        display: 'none',
      },
      '&.Mui-expanded': {
        margin: 0,
        '&:before': {
          display: 'none',
        },
      },
    });

    const sxArray = Array.isArray(sx) ? sx : sx ? [sx] : [];

    if (collapsible) {
      return (
        <Box ref={ref} {...other}>
          <Accordion
            data-testid="expandable-section-accordion"
            expanded={isExpanded}
            onChange={handleChange}
            sx={
              [
                {
                  margin: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    '&:before': { display: 'none' },
                  },
                },
                accordionHeaderStyles(isExpanded),
                ...sxArray,
              ] as SxProps<Theme>
            }
          >
            <AccordionSummary
              expandIcon={showExpandIcon ? <ExpandMoreIcon /> : undefined}
              sx={hasHeader ? getHeaderStyles(isExpanded) : undefined}
              data-testid="expandable-section-summary"
            >
              {summaryContent ?? (
                <Box>
                  {title != null && <Typography variant="h3">{title}</Typography>}
                  {subtitle != null && (
                    <Typography variant="body2" color="text.secondary">
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              )}
              {summaryContent == null && action != null && (
                <Box sx={{ flexShrink: 0 }}>{action}</Box>
              )}
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsStyles}>{children}</AccordionDetails>
          </Accordion>
        </Box>
      );
    }

    return (
      <Box
        ref={ref}
        data-testid="expandable-section"
        sx={[containerStyles, ...sxArray] as SxProps<Theme>}
        {...other}
      >
        {hasHeader && (
          <Box sx={getHeaderStyles(false)}>
            <Box>
              {title != null && <Typography variant="h3">{title}</Typography>}
              {subtitle != null && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {action != null && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
          </Box>
        )}
        {children != null && <Box>{children}</Box>}
      </Box>
    );
  }
);

ExpandableSection.displayName = 'ExpandableSection';
